import React, { useState, useEffect } from "react";
import {
  handleApiExceptions,
  generate_query_set,
  getIndianRupeeNotation,
} from "../../app_methods";
import { EaseTable } from "../../libs/EaseTable";
import { isMobile } from "react-device-detect";
import { getLedgerTableColumns } from "./constants";
import {
  SmartCollectLedger,
  VirtualAccount,
  CustomVirtualAccountLedger,
} from "../../models";
import { connect } from "react-redux";
import FileSaver from "file-saver";
import "./index.scss";
import { debounce, isEmpty } from "lodash";
import { PermissionHoc } from "../HOC";
import moment from "moment";
import { PaginationKaBaap } from "../Common";

const Ledger = (props) => {
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [pagination, setPagination] = useState({
    pageSize: 15,
    current: 1,
    hasNext: true,
    showing: { from: 1, to: 15 } 
});
  const [filters, setFilters] = useState({});
  const [ledgerData, setLedgerData] = useState([]);
  const [virtualAccounts, setVirtualAccounts] = useState([]);
  const [customVirtualAccounts, setCustomVirtualAccounts] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const permissions = props.current_user.user.permissions;

  const prepFilters = (filters) => {
    let _filters = { ...filters };
    if (_filters.created_at && _filters.created_at.length) {
      _filters.created_at_start = _filters.created_at[0].format("YYYY-MM-DD");
      _filters.created_at_end = _filters.created_at[1].format("YYYY-MM-DD");
      delete _filters.created_at;
    }
    return _filters;
  };

  const fetchCustomVirtualAccounts = async (search = null) => {
    if (!props.current_user.key) return;

    setIsTableLoading(true);
    const response = await VirtualAccount.list_v2({
      ...{ pageSize: pagination.pageSize },
      ...{ search: search },
    });
    if (!response?.success) {
      setIsTableLoading(false);
      handleApiExceptions(response);
      return;
    }

    setCustomVirtualAccounts(
      generate_query_set(VirtualAccount, response?.data?.results)
    );
    setIsTableLoading(false);
  };

  const fetchData = (_pagination, _filters) => {
    if (props?.current_user?.ecvabm) {
      fetchCVALedgerData(_pagination, _filters);
    } else {
      fetchSCVALedgerData(_pagination, _filters);
    }
  };

  const fetchCVALedgerData = async (_pagination, _filters) => {
    if (!props.current_user.key) return;
    setIsTableLoading(true);

    const response = await CustomVirtualAccountLedger.list_v2({
      ...{ pageSize: _pagination.pageSize, current: _pagination.current },
      ...prepFilters(_filters),
    });

    if (!response.success) {
      setIsTableLoading(false);
      handleApiExceptions(response);
      return;
    }

    setLedgerData(
      generate_query_set(CustomVirtualAccountLedger, response.data.results)
    );
    setPagination({ ..._pagination, ...response.data.pagination });
    setIsTableLoading(false);
  };

  const fetchSCVALedgerData = async (_pagination, _filters) => {
    if (!props.current_user.key) return;

    setIsTableLoading(true);

    const response = await SmartCollectLedger.list_v2({
      ...{ pageSize: _pagination.pageSize, current: _pagination.current },
      ...prepFilters(_filters),
    });

    if (!response.success) {
      setIsTableLoading(false);
      handleApiExceptions(response);
      return;
    }
    setLedgerData(
      generate_query_set(SmartCollectLedger, response.data.results)
    );
    setPagination({ ..._pagination, ...response.data.pagination });
    setIsTableLoading(false);
  };

  const downloadReport = () => {
    if (props?.current_user?.ecvabm) {
      return downloadCustomVirtualAccountReport();
    } else {
      return downloadSmartCollectReport();
    }
  };

  const downloadCustomVirtualAccountReport = async () => {
    setIsDownloading(true);
    if (filters.created_at.length) {
      if (filters.created_at[1].diff(filters.created_at[0], "days") > 31) {
        handleApiExceptions(
          "This report can be downloaded for 31 days or less."
        );
        setIsDownloading(false);
        return;
      }
    }
    const response = await CustomVirtualAccountLedger.report_download({
      ...prepFilters(filters),
    });
    if (response.status != 200) {
      setIsDownloading(false);
      handleApiExceptions("Something went wrong !");
      return;
    }
    FileSaver.saveAs(response.data, `Virtual Account Statement.csv`);
    setIsDownloading(false);
  };

  const downloadSmartCollectReport = async () => {
    setIsDownloading(true);
    if (filters.created_at.length) {
      if (filters.created_at[1].diff(filters.created_at[0], "days") > 366) {
        handleApiExceptions(
          "This report can be downloaded for up to 1 year only."
        );
        setIsDownloading(false);
        return;
      }
    }
    const response = await SmartCollectLedger.report_download({
      ...prepFilters(filters),
    });
    if (response.status != 200) {
      setIsDownloading(false);
      handleApiExceptions("Something went wrong !");
      return;
    }
    FileSaver.saveAs(response.data, `Insta Collect Account Statement.csv`);
    setIsDownloading(false);
  };

  const handleTableChange = (tableInfo) => {
    if (tableInfo.trigger == "columns") return;
    let _pagination = tableInfo.pagination;
    if (tableInfo.trigger == "filters") _pagination.current = 1;
    setPagination(_pagination);
    setFilters(tableInfo.filters);
    fetchData(_pagination, tableInfo.filters);
  };

  const searchCustomVirtualAccount = debounce((search) => {
    if (search.length > 2) {
      fetchCustomVirtualAccounts(search);
    } else if (search.length === 0) {
      fetchCustomVirtualAccounts();
    }
  }, 500);

  const ledgerTableFilterNodes = (virtualAccounts, customVirtualAccounts) => {
    let nodes = [
      // {
      //     label: "Account No.",
      //     field: "smart_collect_virtual_account",
      //     placeholder: "Select Virtual Account",
      //     inputType: "select",
      //     width: 200,
      //     disabled: (virtualAccounts || []).length <= 1,
      //     preventClear: true,
      //     defaultValue: ((virtualAccounts || []).find(x => x.is_active) || {}).id,
      //     options: (virtualAccounts || []).map((account, idx) => {
      //         return {
      //             key: idx,
      //             value: account.id,
      //             label: account.virtual_account_number
      //         }
      //     })
      // },
      {
        label: "Date",
        field: "created_at",
        placeholder: "Enter Date",
        inputType: "date_range",
        preventClear: true,
        defaultValue: [moment().subtract(3, "days"), moment()],
        disabledDate: moment().subtract(1, "year"),
      },
      {
        label: "Debit/Credit",
        field: "transaction_type",
        placeholder: "Select Transaction Type",
        inputType: "select",
        defaultValue: undefined,
        options: [
          {
            key: "1",
            value: "debit",
            label: "Debit",
          },
          {
            key: "2",
            value: "credit",
            label: "Credit",
          },
        ],
      },
    ];

    if (props.current_user.is_reseller && props.current_user.esda) {
      nodes.unshift({
        label: "Merchant",
        field: "submerchant_id",
        placeholder: "Merchant ID",
        width: 250,
        inputType: "select",
        showSearch: true,
        defaultValue: undefined,
        disabled: (props.current_user.submerchants || []).length <= 0,
        options: props.current_user.submerchants
          ? props.current_user.submerchants.map((sub_merchant, index) => {
              let object = {};
              object.key = index;
              object.value = sub_merchant.id;
              object.label =
                sub_merchant.name + " (" + sub_merchant.email + ")";
              return object;
            })
          : [],
      });
    }

    if (props?.current_user?.ecvabm) {
      nodes.unshift({
        label: "Virtual Account",
        field: "id",
        placeholder: "Select Virtual Account",
        width: 250,
        inputType: "select",
        preventClear: true,
        showSearch: true,
        onSearch: searchCustomVirtualAccount,
        searchMinLength: 3,
        defaultValue: !isEmpty(customVirtualAccounts || [])
          ? customVirtualAccounts[0]?.id
          : undefined,
        disabled: (customVirtualAccounts || []).length <= 0,
        options: customVirtualAccounts
          ? customVirtualAccounts.map((cva, index) => {
              let object = {};
              object.key = index;
              object.value = cva.id;
              object.label = cva.label
              return object;
            })
          : [],
      });
    }

    return nodes;
  };

  const tableColumns = getLedgerTableColumns({});
  const tableFilters =
    ledgerTableFilterNodes(virtualAccounts, customVirtualAccounts) || [];

  const onPageChange = (_pagination) => {
      setPagination({ ..._pagination });
      fetchData({ ..._pagination }, filters);
  };

  useEffect(() => {
    props.onTabRender("account_statement");
    if (props?.current_user?.ecvabm) {
      fetchCustomVirtualAccounts();
    } else {
      setVirtualAccounts(props?.current_user?.smart_collect_virtual_accounts);
    }
  }, []);

  useEffect(() => {
    let defaultFilters = {};
    tableFilters.forEach((f) => (defaultFilters[f.field] = f?.defaultValue));
    setFilters({ ...filters, ...defaultFilters });
    fetchData({ ...pagination }, { ...filters, ...defaultFilters });
  }, [customVirtualAccounts, virtualAccounts]);

  return (
    <>
    <EaseTable
      className="ledger-table-wrapper"
      rowKey="uuid"
      loading={isTableLoading}
      data={ledgerData}
      columns={tableColumns}
      fixedSelectable={!isMobile}
      handleTableChange={handleTableChange}
      // pagination={pagination}
      // totalRecords={pagination.total}
      filterNodes={tableFilters}
      downloadable={
        permissions.includes("ACCOUNT_STATEMENT_DOWNLOAD_REPORT") &&
        props.current_user.allow_dashboard_download
      }
      onDownload={downloadReport}
      isDownloading={isDownloading}
    /><PaginationKaBaap pageChange={onPageChange} pagination={pagination} />
    </>
  );
};

const mapStateToProps = (state) => ({
  current_user: state.userReducer.current_user,
});

export default connect(mapStateToProps)(
  PermissionHoc(Ledger, "IC_ACCOUNT_STATEMENT_READ")
);
